import React, { useEffect, useRef } from 'react';

// ConfirmDialog — diálogo de confirmación compartido de las apps del hub VLS.
//
// FUENTE DE VERDAD. No hay monorepo ni registry (ver
// reports/ui-audit-2026-08-22/plan-de-ataque.md, Tanda 2), así que este
// archivo se COPIA a cada app con scripts/sync-ui.sh, no se importa como
// paquete. Si se edita, correr sync-ui.sh para propagar y NO editar las
// copias de cada app directamente — se pierden en el próximo sync.
//
// Portado de vls-rrhh/src/components/ui/ConfirmDialog.jsx, la referencia que
// señala el plan. Deliberadamente AUTOCONTENIDO: sin depender del `Icon` ni
// del `Button` propios de cada app (varían de nombre, props y tokens de
// color entre los 21 repos) ni de un hook `useFocusTrap` aparte (la carpeta
// de hooks no existe igual en todas). El ícono va como SVG inline, los
// botones son <button> con Tailwind + una variable CSS con fallback
// (var(--color-primary, #0f7a3d)) para que tome el color de marca de la app
// si existe, y si no, el verde VLS por defecto.
//
// Reemplaza los window.confirm()/alert()/prompt() para acciones destructivas
// o irreversibles (borrar, cancelar, dar de baja). NO reemplaza diálogos con
// formulario propio (eso es el Modal compartido, pieza aparte de esta tanda).
//
// Uso:
//   const [confirmando, setConfirmando] = useState(false);
//   const [borrando, setBorrando] = useState(false);
//   <ConfirmDialog
//     open={confirmando}
//     title="¿Borrar este registro?"
//     message="Esta acción no se puede deshacer."
//     destructive
//     loading={borrando}
//     onConfirm={async () => { setBorrando(true); await borrar(); setBorrando(false); setConfirmando(false); }}
//     onCancel={() => setConfirmando(false)}
//   />
//
// Agregado 2026-09-18 al absorber las copias propias de rrhh y horario
// (retrocompatible, ningún default cambia):
//   - children: si no hay `message`, el cuerpo sale de children
//     (<ConfirmDialog ...>texto</ConfirmDialog>, como lo usaba horario).
//   - zIndexClass (por defecto z-50): capa del overlay, para apps cuyo header
//     fijo va por encima de z-50 (rrhh y horario pasan z-modal). Mismo
//     contrato que Modal.jsx.
//   - El overlay lleva la clase no-print: en apps con vista de impresión el
//     diálogo nunca sale en papel. Donde la clase no exista, no hace nada.
// Pila compartida de overlays abiertos (Modal y ConfirmDialog, en todas sus
// copias): solo el de arriba atiende Escape y Tab. Antes los dos escuchaban
// Escape en document y ninguno detenía el evento: Escape en una confirmación
// cerraba también el formulario de abajo y se perdían los cambios (auditoría
// de UI 2026-09-25, rrhh #2). Vive en globalThis porque son dos módulos.
// El orden de la pila es el de apertura: el overlay de arriba tiene que
// abrirse después del de abajo (no en el mismo render), como en todos los
// flujos de hoy.
function pilaOverlays() {
  if (!globalThis.__vlsPilaOverlays) globalThis.__vlsPilaOverlays = [];
  return globalThis.__vlsPilaOverlays;
}

// Mete el overlay en la pila mientras está abierto; devuelve si es el de arriba.
function usePilaOverlay(open) {
  const token = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const propio = {};
    token.current = propio;
    pilaOverlays().push(propio);
    return () => {
      const pila = pilaOverlays();
      const i = pila.indexOf(propio);
      if (i >= 0) pila.splice(i, 1);
    };
  }, [open]);
  return () => {
    const pila = pilaOverlays();
    return pila.length > 0 && pila[pila.length - 1] === token.current;
  };
}

export default function ConfirmDialog({
  open,
  title,
  message,
  children,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  destructive = false,
  loading = false,
  onConfirm,
  onCancel,
  zIndexClass = 'z-50',
}) {
  const containerRef = useRef(null);
  const previouslyFocused = useRef(null);
  const onCancelRef = useRef(onCancel);

  useEffect(() => {
    onCancelRef.current = onCancel;
  });
  // loading entra por ref por el mismo motivo que onCancel: si el efecto de
  // abajo dependiera de él, al confirmar se reiniciaba, el foco salía al botón
  // de la página y volvía, y el lector de pantalla anunciaba de más
  // (auditoría de UI 2026-09-25, rrhh #23).
  const loadingRef = useRef(loading);
  useEffect(() => {
    loadingRef.current = loading;
  });
  const esDeArriba = usePilaOverlay(open);

  // Focus trap + Escape + restaurar el foco al cerrar, inlineado (ver nota de
  // arriba sobre por qué no es un hook aparte).
  //
  // onCancel entra por ref (no como dependencia): el padre casi siempre lo
  // pasa como arrow function inline, así que cada re-render suyo (por
  // ejemplo cada tecla en un input controlado dentro del diálogo) crearía
  // una función nueva y reiniciaría este efecto, robándole el foco de vuelta
  // al contenedor en cada keystroke. Mismo bug que Modal.jsx y Dialog.jsx.
  useEffect(() => {
    if (!open) return undefined;
    previouslyFocused.current = document.activeElement;
    const caja = containerRef.current;
    const FOCUSABLE = 'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';
    const getFocusables = () => Array.from(caja?.querySelectorAll(FOCUSABLE) || []);

    const focusTimer = setTimeout(() => {
      const [first] = getFocusables();
      (first || caja)?.focus?.();
    }, 0);

    function onKeyDown(e) {
      if (!esDeArriba()) return;
      if (e?.key === 'Escape') { if (!loadingRef.current) onCancelRef.current?.(); return; }
      if (e?.key !== 'Tab') return;
      const focusables = getFocusables();
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    document.addEventListener('keydown', onKeyDown);

    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  // Al terminar de cargar (loading de true a false) con el diálogo abierto, por
  // ejemplo tras un error: si el foco quedó fuera (el botón estuvo
  // deshabilitado), vuelve al primer control. El efecto de arriba ya no se
  // repite con loading (revisión 2026-09-26).
  const cargabaRef = useRef(loading);
  useEffect(() => {
    const cargaba = cargabaRef.current;
    cargabaRef.current = loading;
    // Solo si es el overlay de arriba: no le quita el foco a uno abierto encima.
    if (!open || !cargaba || loading || !esDeArriba()) return;
    const caja = containerRef.current;
    if (caja && !caja.contains(document.activeElement)) {
      const primero = caja.querySelector('button:not([disabled]),a[href],input:not([disabled]),textarea:not([disabled]),select:not([disabled])');
      (primero || caja).focus?.();
    }
  }, [open, loading]);

  if (!open) return null;

  const accent = destructive ? '#B23B2E' : 'var(--color-primary, #0f7a3d)';

  return (
    <div
      className={`no-print fixed inset-0 ${zIndexClass} flex items-center justify-center bg-black/40 p-[16px] pb-[max(env(safe-area-inset-bottom,0px),1rem)]`}
      onMouseDown={(e) => { if (e.target === e.currentTarget && !loading) onCancel?.(); }}
    >
      <div
        ref={containerRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="vls-confirm-title"
        aria-describedby="vls-confirm-message"
        // Medidas en píxeles fijos: en las apps que redefinen la escala al doble,
        // sm:p-6 eran 48 px y los botones px-4 32 px por lado, y "Seguir editando"
        // y "Descartar cambios" se partían en dos renglones (auditoría de UI
        // 2026-09-25, horario #4).
        // En teléfono: p-5 (p-6 dejaba la tarjeta de 311px con muy poco ancho
        // útil para el mensaje) y tope de alto con scroll propio, para que un
        // mensaje largo o una pantalla baja (teclado abierto detrás) no dejen
        // los botones fuera de la vista. Auditoría móvil 2026-09-19.
        className="bg-white border rounded-xl w-full max-w-sm p-[20px] sm:p-[24px] shadow-lg max-h-[90dvh] overflow-y-auto"
        style={{ borderColor: 'var(--color-border, #E5E7EB)' }}
      >
        <div className="flex items-center gap-2 mb-2">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <h2 id="vls-confirm-title" className="font-semibold text-lg tracking-tight"
              style={{ color: 'var(--color-foreground, #14201A)' }}>
            {title}
          </h2>
        </div>
        <p id="vls-confirm-message" className="text-sm mb-5" style={{ color: 'var(--color-muted-foreground, #6B7A70)' }}>
          {message ?? children}
        </p>
        {/* Si no caben en una fila (teléfono), cada botón toma el ancho. */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            disabled={loading}
            onClick={() => onCancel?.()}
            className="min-h-11 flex-1 sm:flex-none whitespace-nowrap px-[16px] rounded-md border text-sm font-medium disabled:opacity-50"
            style={{ borderColor: 'var(--color-border, #E5E7EB)', color: 'var(--color-foreground, #14201A)' }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={() => onConfirm?.()}
            className="min-h-11 flex-1 sm:flex-none whitespace-nowrap px-[16px] rounded-md text-sm font-semibold text-white disabled:opacity-60"
            style={{ background: accent }}
          >
            {loading ? 'Espere…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
